import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getData, postData } from "../../network/api-client";
import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import {
  User,
  Mail,
  Github,
  Linkedin,
  FileText,
  Upload,
  Trash2,
  Pencil,
  X,
  Save,
  Building,
  Calendar,
  GraduationCap,
  Download,
  Plus,
  Search,
  UploadCloud,
  Check,
  CheckCircle2,
  ChevronDown,
  Briefcase,
  MapPin,
} from "lucide-react";
import {
  FETCH_EDUCATION_PROFILE,
  GET_USER_PROFILE,
  GET_USER_DETAILS,
  UPDATE_USER_PROFILE_V3,
  GET_COURSES_LIST,
  GET_COURSE_SPECIALIZATION_LIST,
  CREATE_EDUCATION_PROFILE,
  UPDATE_EDUCATION_PROFILE,
  UPLOAD_RESUME,
  DELETE_RESUME,
  UPDATE_USER_IMAGE,
  GET_COLLEGES_LIST,
  GET_LOCATIONS_LIST,
  GET_USER_PREFFERED_LOCATIONS,
  UPDATE_CURRENT_LOCATION,
  UPDATE_USER_LOCATION,
} from "../../shared/constants/routing-constants";

// --- MOCK DATA ---
const COURSE_OPTIONS = [
  "B.Tech",
  "B.E.",
  "B.Sc",
  "B.Com",
  "B.A.",
  "MBA",
  "M.Tech",
  "MCA",
];

const QUALIFICATION_OPTIONS = [
  { id: 1, label: "UG", value: "UG" },
  { id: 2, label: "PG", value: "PG" },
  { id: 3, label: "SSC", value: "SSC" },
  { id: 4, label: "ITI", value: "ITI" },
  { id: 5, label: "HSC", value: "HSC" },
  { id: 6, label: "DIPLOMA", value: "DIPLOMA" },
  { id: 7, label: "ICWA/CA", value: "ICWA/CA" },
  { id: 8, label: "DOCTORS", value: "DOCTORS" },
  { id: 9, label: "PHD", value: "PHD" },
  { id: 10, label: "OTHER", value: "OTHER" },
];

const SPECIALIZATION_MAP = {
  "B.Tech": [
    "Computer Science",
    "Information Technology",
    "Electronics",
    "Mechanical",
    "Civil",
  ],
  "B.E.": [
    "Computer Science",
    "Electronics & Comm",
    "Electrical",
    "Mechanical",
  ],
  "B.Sc": ["Physics", "Chemistry", "Mathematics", "Computer Science"],
  "B.Com": ["General", "Accounting", "Finance"],
  "B.A.": ["English", "History", "Economics"],
  MBA: ["Marketing", "Finance", "HR", "Operations"],
  "M.Tech": ["Computer Science", "VLSI", "Power Systems"],
  MCA: ["Computer Applications"],
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarStyle, setSnackbarStyle] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eduToDelete, setEduToDelete] = useState(null);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const showSnackbar = (message, severity = "success", style = {}) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarStyle(style);
    setOpenSnackbar(true);
  };

  // --- STATE: USER INFO ---
  const [user, setUser] = useState({
    experience: "",
    avatar:
      "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png",
  });
  // avtar: ttps://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=256

  const [userImageFile, setUserImageFile] = useState(null);
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const fileInputRef = useRef(null);
  const [mobileError, setMobileError] = useState("");
  const [nameError, setNameError] = useState("");

  // --- STATE: EDUCATION ---
  const [educationList, setEducationList] = useState([]);
  const userData = localStorage.getItem("Freshment_user_data");
  const userdetail = userData ? JSON.parse(userData) : null;
  // console.log("userdetail parsed", userdetail);
  const isGoogleLogin = userdetail?.data?.LoginBy === "GM";

  useEffect(() => {
    if (userdetail === null) {
      navigate("/");
    }
  }, [userdetail, navigate]);

  useEffect(() => {
    const fetchEducationData = async () => {
      try {
        const response = await getData(
          `${GET_USER_PROFILE}/${FETCH_EDUCATION_PROFILE}`,
          { user_id: userdetail?.data?.id },
        );
        if (response && response.data) {
          const fetchedData = Array.isArray(response.data)
            ? response.data
            : [response.data];
          //  const mappedData = fetchedData
          setEducationList(fetchedData);
        } else if (response) {
          // Fallback in case the response itself is the array
          const fetchedData = Array.isArray(response) ? response : [];
          if (fetchedData.length > 0) {
            // const mappedData = fetchedData.map((item, index) => ({
            //   id: item.id || item.edu_id || index + 1,
            //   course: item.course || item.degree || item.qualification || "Unknown Course",
            //   specialization: item.specialization || item.stream || item.major || "Unknown Specialization",
            //   college: item.college || item.institute || item.university || "Unknown College",
            //   year: item.year || item.passing_year || item.grad_year || "2024",
            //   type: item.type || item.education_type || "Full Time",
            //   ...item
            // }));
            setEducationList(fetchedData);
          }
        }
      } catch (error) {
        console.error("Error fetching education profile:", error);
      }
    };
    fetchEducationData();
  }, []);

  // --- STATE: COURSE OPTIONS ---
  const [courseList, setCourseList] = useState([]); // Store full objects
  const [courseOptions, setCourseOptions] = useState(COURSE_OPTIONS); // Store just names for UI fallback

  // --- STATE: SPECIALIZATION OPTIONS ---
  const [specializationOptions, setSpecializationOptions] = useState([]);
  const [specializationList, setSpecializationList] = useState([]);

  const fetchCoursesData = async (eduStandard = "UG") => {
    try {
      const response = await getData(
        `${GET_USER_PROFILE}/${GET_COURSES_LIST}`,
        { edu_standard: eduStandard },
      );
      if (response && response.data && Array.isArray(response.data)) {
        // console.log("Courses List:", response.data);
        setCourseList(response.data);
        setCourseOptions(response.data.map((c) => c.name));
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching courses list:", error);
    }
    return [];
  };

  useEffect(() => {
    fetchCoursesData("UG");
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userId = userdetail?.data?.id;
        const response = await getData(`${GET_USER_DETAILS}`, {
          user_id: userId,
        });
        if (response && response.success && response.data) {
          // console.log("User detail", response);
          const profileData = response.data;
          setUser((prev) => ({
            ...prev,
            name: profileData.name || prev.name,
            email: profileData.email || prev.email,
            mobile: profileData.mobile || prev.mobile,
            github: profileData.github_profile || prev.github,
            linkedin: profileData.linkedin_profile || prev.linkedin,
            avatar: profileData.user_image || prev.avatar,
            experience: profileData.experience || prev.experience || "",
          }));

          if (profileData.resume_url) {
            const fileName =
              profileData.resume_url.split("/").pop().split("?")[0] ||
              "Uploaded_Resume.pdf";

            setResume({
              name: fileName,
              size: "Calculating...",
              date: profileData.resume_updated_at
                ? new Date(profileData.resume_updated_at).toLocaleDateString(
                  "en-GB",
                  { day: "numeric", month: "short", year: "numeric" },
                )
                : new Date().toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                }),
              url: profileData.resume_url,
            });

            // Attempt to fetch file size asynchronously
            fetch(profileData.resume_url, { method: "HEAD" })
              .then((res) => {
                if (res.ok) {
                  const contentLength = res.headers.get("content-length");
                  if (contentLength) {
                    const sizeMB = (
                      parseInt(contentLength, 10) /
                      (1024 * 1024)
                    ).toFixed(2);
                    setResume((prev) =>
                      prev ? { ...prev, size: `${sizeMB} MB` } : prev,
                    );
                  } else {
                    setResume((prev) =>
                      prev ? { ...prev, size: "Unknown Size" } : prev,
                    );
                  }
                } else {
                  setResume((prev) =>
                    prev ? { ...prev, size: "Unknown Size" } : prev,
                  );
                }
              })
              .catch(() => {
                setResume((prev) =>
                  prev ? { ...prev, size: "Unknown Size" } : prev,
                );
              });
          } else {
            setResume(null);
          }
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };
    fetchUserProfile();
  }, []);

  const fetchSpecializations = async (courseId, preserveSpec = false) => {
    try {
      if (!courseId) return;
      const response = await getData(
        `${GET_USER_PROFILE}/${GET_COURSE_SPECIALIZATION_LIST}`,
        { course_id: courseId },
      );
      if (response && response.data && Array.isArray(response.data)) {
        setSpecializationList(response.data);
        const specs = response.data.map((s) => s.name);
        setSpecializationOptions(specs);
        setEduForm((prev) => {
          let currentSpec = prev.specialization_name || prev.specialization;
          if (preserveSpec && currentSpec && specs.includes(currentSpec)) {
            return { ...prev, specialization_name: currentSpec };
          }
          return {
            ...prev,
            specialization_name: response.data.length
              ? response.data[0].name
              : "",
          };
        });
      } else {
        setSpecializationList([]);
        setSpecializationOptions([]);
        setEduForm((prev) => ({ ...prev, specialization_name: "" }));
      }
    } catch (error) {
      console.error("Error fetching specializations list:", error);
      setSpecializationList([]);
      setSpecializationOptions([]);
    }
  };

  const [isAddingEdu, setIsAddingEdu] = useState(false);
  const [editingEduId, setEditingEduId] = useState(null);

  // Form State for Add/Edit
  const [eduForm, setEduForm] = useState({
    qualification: "UG",
    course: "",
    course_name: "",
    specialization: "",
    specialization_name: "",
    college: "",
    college_master_id: "",
    year: "2024",
    type: "Full Time",
    passout_year: "2024",
    course_type: "FULLTIME",
  });

  // work location prefrence
  const [isEditingLocations, setIsEditingLocations] = useState(false);
  const [locationsList, setLocationsList] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [currentLocationSearch, setCurrentLocationSearch] = useState("");
  const [preferredLocationSearch, setPreferredLocationSearch] = useState("");
  const [showCurrentLocationDropdown, setShowCurrentLocationDropdown] = useState(false);
  const [showPreferredLocationDropdown, setShowPreferredLocationDropdown] = useState(false);
  const [selectedCurrentLocationId, setSelectedCurrentLocationId] = useState(null);
  const [selectedCurrentLocationCity, setSelectedCurrentLocationCity] = useState("");
  const [currentLocationDisplay, setCurrentLocationDisplay] = useState("");
  const [userPreferredLocations, setUserPreferredLocations] = useState([]);
  const [tempPreferredLocations, setTempPreferredLocations] = useState([]);
  const [originalCurrentLocationId, setOriginalCurrentLocationId] = useState(null);
  const [originalPreferredLocations, setOriginalPreferredLocations] = useState([]);

  // Fetch locations from API
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLocationsLoading(true);
        const response = await getData(GET_LOCATIONS_LIST);
        console.log("Locations response:", response);
        if (response && response.data && Array.isArray(response.data)) {
          setLocationsList(response.data);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, []);

  // Fetch user's current and preferred locations
  useEffect(() => {
    const fetchUserLocations = async () => {
      try {
        const response = await getData(GET_USER_PREFFERED_LOCATIONS, {
          user_id: userdetail?.data?.id
        });
        console.log("User locations response:", response);
        if (response && response.success) {
          // Handle current location
          if (response.current_location_id) {
            const currentLocationId = response.current_location_id;
            const currentLocation = locationsList.find(loc => loc.id === currentLocationId);
            if (currentLocation) {
              setSelectedCurrentLocationId(currentLocationId);
              setSelectedCurrentLocationCity(currentLocation.city);
              setCurrentLocationSearch(currentLocation.city);
              setCurrentLocationDisplay(currentLocation.city);
            }
          }
          // Handle preferred locations - check different possible response structures
          let preferredLocs = [];
          if (response.preferred_locations && Array.isArray(response.preferred_locations)) {
            preferredLocs = response.preferred_locations;
          } else if (response.data && Array.isArray(response.data)) {
            preferredLocs = response.data;
          } else if (response.data && response.data.preferred_locations && Array.isArray(response.data.preferred_locations)) {
            preferredLocs = response.data.preferred_locations;
          }

          console.log("Preferred locations from API:", preferredLocs);

          if (preferredLocs.length > 0) {
            const mappedLocs = preferredLocs.map(prefLoc => {
              const location = locationsList.find(loc => loc.id === prefLoc.id);
              return location ? { id: location.id, city: location.city } : null;
            }).filter(loc => loc !== null);
            console.log("Mapped preferred locations:", mappedLocs);
            setUserPreferredLocations(mappedLocs);
          }
        }
      } catch (error) {
        console.error("Error fetching user locations:", error);
      }
    };
    if (locationsList.length > 0) {
      fetchUserLocations();
    }
  }, [locationsList, userdetail?.data?.id]);

  // Filter locations based on search
  const filteredCurrentLocations = locationsList.filter(location =>
    location.city.toLowerCase().includes(currentLocationSearch.toLowerCase())
  );

  const filteredPreferredLocations = locationsList.filter(location =>
    location.city.toLowerCase().includes(preferredLocationSearch.toLowerCase()) &&
    !tempPreferredLocations.some(loc => loc.city === location.city)
  );

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.location-dropdown')) {
        setShowCurrentLocationDropdown(false);
        setShowPreferredLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update current location API call
  const handleUpdateCurrentLocation = async (locationId, cityName) => {
    try {
      const response = await postData(UPDATE_CURRENT_LOCATION, {
        user_id: userdetail?.data?.id,
        current_location_id: locationId
      });
      console.log("current location response", response)
      if (response && response.success) {
        setCurrentLocationSearch(cityName);
        setCurrentLocationDisplay(cityName);
        setShowCurrentLocationDropdown(false);
        showSnackbar("Current location updated successfully", "success");
      } else {
        showSnackbar("Failed to update current location", "error");
      }
    } catch (error) {
      console.error("Error updating current location:", error);
      showSnackbar("Failed to update current location", "error");
    }
  };

  // Update preferred locations API call
  const handleUpdatePreferredLocations = async () => {
    try {
      const locationIds = tempPreferredLocations.map(loc => loc.id).join(',');
      const response = await postData(UPDATE_USER_LOCATION, {
        user_id: userdetail?.data?.id,
        location_ids: locationIds
      });
      console.log("preferred locations response", response)
      if (response && response.success) {
        setUserPreferredLocations([...tempPreferredLocations]);
        showSnackbar("Preferred locations updated successfully", "success", { marginTop: "20px" });
      } else {
        showSnackbar("Failed to update preferred locations", "error", { marginTop: "20px" });
      }
    } catch (error) {
      console.error("Error updating preferred locations:", error);
      showSnackbar("Failed to update preferred locations", "error", { marginTop: "20px" });
    }
  };

  // College Search State
  const [collegeSearch, setCollegeSearch] = useState("");
  const [showCollegeDropdown, setShowCollegeDropdown] = useState(false);
  const [collegeResults, setCollegeResults] = useState([]);
  const [collegeLoading, setCollegeLoading] = useState(false);

  // Debounced live college search via API
  useEffect(() => {
    if (!collegeSearch || collegeSearch.length < 2) {
      setCollegeResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setCollegeLoading(true);
        const response = await getData(GET_COLLEGES_LIST, {
          search: collegeSearch,
        });
        // API returns { data: [ { id, name, ... }, ... ] } or an array directly
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setCollegeResults(list);
        // console.log("College Search Results:", list);
        // store full objects: { id, college_name }
      } catch (e) {
        console.error("College search error:", e);
        setCollegeResults([]);
      } finally {
        setCollegeLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [collegeSearch]);

  // --- STATE: RESUME ---
  const [resume, setResume] = useState(null);
  const resumeInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // --- HANDLERS: PHOTO ---
  const handlePhotoClick = () => {
    if (!isEditingInfo) return;
    fileInputRef.current?.click();
  };

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      setUser({ ...user, avatar: imageUrl });
      setUserImageFile(file);
    }
  };

  const handleProfileSave = async () => {
    // Validate before saving
    if (nameError || mobileError) {
      showSnackbar("Please fix validation errors before saving.", "error");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("user_id", userdetail?.data?.id?.toString() || "10");
      if (user.name) formData.append("name", user.name);
      if (user.mobile) formData.append("mobile", user.mobile);
      if (user.linkedin !== undefined)
        formData.append("linkedin_profile", user.linkedin);
      if (user.github !== undefined)
        formData.append("github_profile", user.github);
      if (user.experience !== undefined)
        formData.append("experience", user.experience);

      // console.log("userImageFile:", userImageFile);
      const res = await postData(UPDATE_USER_PROFILE_V3, formData);
      // console.log("Update Profile Response:", res);

      if (res && res.success === false) {
        showSnackbar(res.message || "Failed to update profile", "error");
        return;
      }
      if (userImageFile) {
        const userFormData = new FormData();
        userFormData.append(
          "user_id",
          userdetail?.data?.id?.toString() || "10",
        );
        userFormData.append("image", userImageFile);
        const res = await postData(UPDATE_USER_IMAGE, userFormData);
        // console.log("Update Profile Response:", res);
      }

      setIsEditingInfo(false);
      setUserImageFile(null);
      showSnackbar(
        res?.message || "Profile info saved successfully.",
        "success",
      );
    } catch (error) {
      console.error("Failed to save profile:", error);
      showSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        "Error saving profile details",
        "error",
      );
    }
  };

  // --- HANDLERS: RESUME ---
  const handleResumeClick = () => {
    resumeInputRef.current?.click();
  };

  const uploadResume = async (file) => {
    try {
      if (file.size > 2 * 1024 * 1024) {
        showSnackbar("File size should be less than 2MB", "error");
        return;
      }

      const formData = new FormData();
      formData.append("user_id", userdetail?.data?.id?.toString() || "10");
      formData.append("resume_file", file);

      const res = await postData(
        `${GET_USER_PROFILE}/${UPLOAD_RESUME}`,
        formData,
      );
      // console.log("Upload Resume Response:", res);

      const fileUrl = res?.data?.resume_url || URL.createObjectURL(file);

      setResume({
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2) + " MB",
        date: new Date().toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }),
        url: fileUrl,
      });
      showSnackbar("Resume uploaded successfully", "success");
    } catch (error) {
      console.error("Failed to upload resume:", error);
      showSnackbar("Error uploading resume", "error");
    }
  };

  const ALLOWED_RESUME_TYPES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const isValidResumeType = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    return (
      (ALLOWED_RESUME_TYPES.includes(file.type) ||
        ext === "pdf" ||
        ext === "docx") &&
      ext !== "doc"
    );
  };

  const handleResumeChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!isValidResumeType(file)) {
        showSnackbar("Only PDF and DOCX files are allowed.", "error");
        e.target.value = "";
        return;
      }
      await uploadResume(file);
    }
  };

  const handleResumeDelete = async (e) => {
    e.stopPropagation();
    try {
      const userId = userdetail?.data?.id?.toString() || "10";
      const res = await getData(`${GET_USER_PROFILE}/${DELETE_RESUME}`, {
        user_id: userId,
      });
      // console.log("Delete Resume Response:", res);
      setResume(null);
      showSnackbar("Resume deleted successfully", "success");
    } catch (error) {
      console.error("Failed to delete resume:", error);
      showSnackbar("Error deleting resume", "error");
    }
  };

  const handleResumeDownload = async () => {
    if (resume?.url) {
      try {
        const response = await fetch(resume.url);
        if (!response.ok) throw new Error("Network response was not ok");
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", resume.name || "resume.pdf");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download failed:", error);
        showSnackbar("Failed to download resume.", "error");
      }
    } else {
      showSnackbar("Resume URL not available for download.", "error");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (!isValidResumeType(file)) {
        showSnackbar("Only PDF and DOCX files are allowed.", "error");
        return;
      }
      await uploadResume(file);
    }
  };

  // --- HANDLERS: EDUCATION ---
  const handleEditEdu = async (edu) => {
    setEditingEduId(edu.id);
    setIsAddingEdu(false);

    const qualification = edu.education_standard || "UG";
    const courses = await fetchCoursesData(qualification);

    setEduForm({
      ...edu,
      qualification: qualification,
      course_name: edu.course_name || edu.course || "",
      specialization_name: edu.specialization_name || edu.specialization || "",
    });
    // console.log("Selected Education for Edit:", edu);
    setCollegeSearch(edu.college_name || edu.college || "");
    const courseName = edu.course_name || edu.course || "";
    const courseObj = courses
      ? courses.find((c) => c.name === courseName)
      : null;
    // console.log("courseObj", courseObj);
    if (courseObj) {
      fetchSpecializations(courseObj.id, true);
    } else if (courseName && SPECIALIZATION_MAP[courseName]) {
      setSpecializationOptions(SPECIALIZATION_MAP[courseName]);
    }
  };

  const handleAddNewClick = () => {
    setEditingEduId(null);
    setIsAddingEdu(true);

    const defaultQualification = "UG";
    // Default to the first course
    const defaultCourseName = courseOptions.length ? courseOptions[0] : "";
    setEduForm({
      qualification: defaultQualification,
      course_name: defaultCourseName,
      specialization_name: "", // Will be set by fetchSpecializations
      college: "",
      passout_year: "2024",
      course_type: "FULLTIME",
    });

    fetchCoursesData(defaultQualification).then((courses) => {
      if (courses && courses.length > 0) {
        setEduForm((prev) => ({
          ...prev,
          course_name: courses[0].name,
        }));
        fetchSpecializations(courses[0].id);
      } else if (defaultCourseName && SPECIALIZATION_MAP[defaultCourseName]) {
        // Fallback map
        setSpecializationOptions(SPECIALIZATION_MAP[defaultCourseName]);
        setEduForm((prev) => ({
          ...prev,
          specialization_name: SPECIALIZATION_MAP[defaultCourseName][0],
        }));
      } else {
        setSpecializationOptions([]);
      }
    });

    setCollegeSearch("");
  };

  const handleCancelEdu = () => {
    setEditingEduId(null);
    setIsAddingEdu(false);
    setShowCollegeDropdown(false);
  };

  const handleSaveEdu = async () => {
    // console.log("Saving Education with form data:", eduForm);
    if (
      !(eduForm.course_name || eduForm.course) ||
      !(eduForm.college_name || eduForm.college)
    ) {
      showSnackbar("Please fill all required fields", "warning");
      return;
    }


    if (!eduForm.college_master_id) {
      showSnackbar("Please select a valid college from the search results", "warning");
      return;
    }

    try {
      const course = courseList.find(
        (c) => c.name === (eduForm.course_name || eduForm.course),
      );
      // console.log("course", course);
      const courseId = course ? course.id : "18"; // default from curl

      const spec = specializationList.find(
        (s) =>
          s.name === (eduForm.specialization_name || eduForm.specialization),
      );
      const specId = spec ? spec.id : "355"; // default from curl

      const formData = new FormData();
      formData.append("user_id", userdetail?.data?.id?.toString()); // default value according to curl
      formData.append("course_id", courseId.toString());
      formData.append("specialization_id", specId.toString());
      formData.append(
        "college_master_id",
        (eduForm.college_master_id || "8645").toString(),
      );
      formData.append("start_year", eduForm.start_year?.toString() || "2024");
      formData.append(
        "passout_year",
        eduForm.passout_year?.toString() || eduForm.year?.toString() || "2026",
      );
      formData.append(
        "course_type",
        eduForm.course_type || eduForm.type === "Part Time"
          ? "PARTTIME"
          : "FULLTIME",
      );

      let res;
      if (editingEduId) {
        formData.append("id", editingEduId.toString());
        res = await postData(
          `${GET_USER_PROFILE}/${UPDATE_EDUCATION_PROFILE}`,
          formData,
        );
      } else {
        res = await postData(
          `${GET_USER_PROFILE}/${CREATE_EDUCATION_PROFILE}`,
          formData,
        );
      }
      // console.log("Save Response:", res);

      // Normalize: list UI reads `college_name`, form stores it as `college`
      const normalizedEdu = {
        ...eduForm,
      };

      if (editingEduId) {
        // Update existing
        setEducationList((prev) =>
          prev.map((item) =>
            item.id === editingEduId
              ? { ...normalizedEdu, id: editingEduId }
              : item,
          ),
        );
      } else {
        // Add new
        const newId =
          educationList.length > 0
            ? Math.max(...educationList.map((e) => e.id), 0) + 1
            : 1;
        setEducationList([...educationList, { ...normalizedEdu, id: newId }]);
      }
      handleCancelEdu();
    } catch (error) {
      console.error("Failed to save education profile:", error);
      showSnackbar("Error saving education details", "error");
    }
  };

  const handleDeleteEdu = (id) => {
    setEduToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteEdu = () => {
    if (eduToDelete) {
      setEducationList((prev) =>
        prev.filter((item) => item.id !== eduToDelete),
      );
      setEduToDelete(null);
    }
    setDeleteDialogOpen(false);
  };

  const cancelDeleteEdu = () => {
    setEduToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleQualificationChange = async (e) => {
    const newQual = e.target.value;
    setEduForm((prev) => ({
      ...prev,
      qualification: newQual,
      course_name: "Loading...",
      specialization_name: "",
    }));

    const courses = await fetchCoursesData(newQual);
    if (courses && courses.length > 0) {
      setEduForm((prev) => ({
        ...prev,
        course_name: courses[0].name,
      }));
      fetchSpecializations(courses[0].id);
    } else {
      setEduForm((prev) => ({
        ...prev,
        course_name: "",
        specialization_name: "",
      }));
      setSpecializationOptions([]);
    }
  };

  // Update Specialization when Course changes
  const handleCourseChange = (e) => {
    const newCourse = e.target.value;

    setEduForm((prev) => ({
      ...prev,
      course_name: newCourse,
      specialization_name: "Loading...", // Temporary state while fetching
    }));

    const courseObj = courseList.find((c) => c.name === newCourse);

    if (courseObj) {
      fetchSpecializations(courseObj.id);
    } else {
      // Fallback
      const availableSpecs = SPECIALIZATION_MAP[newCourse] || [];
      setSpecializationOptions(availableSpecs);
      setEduForm((prev) => ({
        ...prev,
        specialization_name: availableSpecs[0] || "",
      }));
    }
  };
  const selectCollege = (college) => {
    setEduForm({
      ...eduForm,
      college: college.college_name,
      college_master_id: college.id,
    });
    setCollegeSearch(college.college_name);
    setShowCollegeDropdown(false);
  };

  // work location preference


  return (
    <div className="min-h-screen bg-slate-50/50 pt-8 pb-20 font-sans">
      <div className="max-w-6xl mx-auto px-8 xl:px-6 xsm:px-4">
        {/* Header */}
        <div className="flex flex-row sm:flex-col items-end sm:items-start justify-between gap-4 mb-10">
          <div>
            <h1 className="text-4xl sm:text-3xl font-black text-slate-900 tracking-tight">
              My Profile
            </h1>
            <p className="text-slate-500 font-medium mt-1">
              Manage your career profile and settings
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm font-bold text-slate-400 bg-white px-4 py-2 rounded-full border border-slate-200 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            Profile Active
          </div>
        </div>

        <div className="grid grid-cols-12 xl:grid-cols-1 gap-8">
          {/* LEFT COLUMN: Personal Info & Education (8 cols) */}
          <div className="col-span-8 xl:col-span-1 space-y-8">
            {/* 1. Personal Information */}
            <div className="bg-white rounded-[2rem] p-8 sm:p-6 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.02)] relative overflow-hidden group">
              <div className="flex justify-between items-start mb-8 relative z-10">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2.5">
                  <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                    <User className="w-5 h-5" />
                  </div>
                  Personal Details
                </h2>
                <button
                  onClick={() => setIsEditingInfo(!isEditingInfo)}
                  className={`text-sm font-bold px-4 py-2 rounded-xl transition-all flex items-center gap-2 ${isEditingInfo ? "bg-slate-100 text-slate-600 hover:bg-slate-200" : "text-blue-600 hover:bg-blue-50"}`}
                >
                  {isEditingInfo ? (
                    <>
                      <X className="w-4 h-4" /> Cancel
                    </>
                  ) : (
                    <>
                      <Pencil className="w-4 h-4" /> Edit
                    </>
                  )}
                </button>
              </div>

              <div className="flex flex-row sm:flex-col gap-8 items-start relative z-10">
                {/* Avatar Upload */}
                <div
                  className={`flex-shrink-0 relative mx-0 sm:mx-auto ${isEditingInfo && !isGoogleLogin ? "group/avatar cursor-pointer" : ""}`}
                  onClick={() => !isGoogleLogin && handlePhotoClick()}
                >
                  <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden relative">
                    <img
                      src={user?.avatar}
                      alt="Profile"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src =
                          "https://images.unsplash.com/photo-1557683311-eac922347aa1?auto=format&fit=crop&q=80&w=256";
                      }}
                      className={`w-full h-full object-cover transition-transform ${isEditingInfo && !isGoogleLogin ? "group-hover/avatar:scale-105" : ""}`}
                    />
                    {isEditingInfo && !isGoogleLogin && (
                      <div className="absolute inset-0 bg-slate-900/30 flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity backdrop-blur-[2px]">
                        <Upload className="w-8 h-8 text-white drop-shadow-md" />
                      </div>
                    )}
                  </div>
                  {isEditingInfo && !isGoogleLogin && (
                    <div className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full border-4 border-white shadow-md">
                      <Pencil className="w-3.5 h-3.5" />
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                </div>

                {/* Form Fields */}
                <div className="flex-1 w-full grid grid-cols-2 sm:grid-cols-1 gap-x-6 gap-y-5">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Full Name
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingInfo || isGoogleLogin}
                      value={user.name}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow only letters, spaces, hyphens, apostrophes, and underscores
                        const isValid = /^[a-zA-Z\s\-'_]*$/.test(value);
                        // Check for HTML tags or script patterns
                        const hasHtmlTags = /<[a-zA-Z][^>]*>|<\/[a-zA-Z][^>]*>|javascript:|on\w+\s*=/.test(value);

                        if (!isValid || hasHtmlTags) {
                          setNameError("Profile Name can only contain letters and spaces. Special Characters, HTML tags and scripts are not allowed.");
                        } else {
                          setNameError("");
                        }

                        // Only update if valid
                        if (isValid && !hasHtmlTags) {
                          setUser({ ...user, name: value });
                        }
                      }}
                      className={`w-full p-3 rounded-xl border ${isEditingInfo && !isGoogleLogin && nameError ? "border-red-500 bg-red-50 focus:ring-2 focus:ring-red-500/20 focus:border-red-500" : isEditingInfo && !isGoogleLogin ? "border-slate-300 bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" : "border-slate-100 bg-slate-50 text-slate-800"} text-sm font-bold transition-all`}
                    />
                    {nameError && (
                      <p className="text-xs text-red-500 font-medium">{nameError}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Mobile
                    </label>
                    <input
                      type="text"
                      disabled={!isEditingInfo}
                      value={user.mobile}
                      minLength={10}
                      maxLength={10}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, "");
                        setUser({ ...user, mobile: value });

                        if (value.length === 0) {
                          setMobileError("");
                          return;
                        }

                        if (value.length !== 10) {
                          setMobileError("Please enter a valid 10-digit mobile number.");
                          return;
                        }

                        // Check if first digit is valid (6-9 for Indian mobile numbers)
                        if (!/^[6-9]/.test(value)) {
                          setMobileError("Mobile number must start with 6, 7, 8, or 9.");
                          return;
                        }

                        // Check if all digits are the same (e.g., 0000000000, 1111111111)
                        if (/^(\d)\1{9}$/.test(value)) {
                          setMobileError("Invalid mobile number: repeated digits are not allowed.");
                          return;
                        }

                        setMobileError("");
                      }}
                      className={`w-full p-3 rounded-xl border ${isEditingInfo && mobileError ? "border-red-500 bg-red-50 focus:ring-2 focus:ring-red-500/20 focus:border-red-500" : isEditingInfo ? "border-slate-300 bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" : "border-slate-100 bg-slate-50 text-slate-800"} text-sm font-bold transition-all`}
                    />
                    {mobileError && (
                      <p className="text-xs text-red-500 font-medium">{mobileError}</p>
                    )}
                  </div>

                  <div className="space-y-1.5 col-span-2 sm:col-span-1">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Email (Read Only)
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        readOnly
                        value={user.email}
                        className="w-full pl-9 p-3 rounded-xl border border-slate-100 bg-slate-50 text-slate-500 text-sm font-bold cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                      LinkedIn
                    </label>
                    <div className="relative">
                      <Linkedin className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        disabled={!isEditingInfo}
                        value={user.linkedin}
                        onChange={(e) =>
                          setUser({ ...user, linkedin: e.target.value })
                        }
                        className={`w-full pl-9 p-3 rounded-xl border ${isEditingInfo ? "border-slate-300 bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" : "border-slate-100 bg-slate-50 text-slate-800"} text-sm font-bold transition-all`}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                      GitHub
                    </label>
                    <div className="relative">
                      <Github className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        disabled={!isEditingInfo}
                        value={user.github}
                        onChange={(e) =>
                          setUser({ ...user, github: e.target.value })
                        }
                        className={`w-full pl-9 p-3 rounded-xl border ${isEditingInfo ? "border-slate-300 bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" : "border-slate-100 bg-slate-50 text-slate-800"} text-sm font-bold transition-all`}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Experience
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                      <select
                        name="Experience"
                        disabled={!isEditingInfo}
                        value={user.experience}
                        onChange={(e) =>
                          setUser({ ...user, experience: e.target.value })
                        }
                        className={`w-full pl-9 p-3 appearance-none rounded-xl border ${isEditingInfo
                          ? "border-slate-300 bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          : "border-slate-100 bg-slate-50 text-slate-800"
                          } text-sm font-bold transition-all`}
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="fresher">Fresher</option>
                        <option value="entry_level">Entry level(0-2)</option>
                        <option value="mid_level">Mid level(2-5)</option>
                        <option value="senior_level">Senior level(7-10)</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  </div>

                  {isEditingInfo && (
                    <div className="col-span-2 sm:col-span-1 flex justify-end mt-2">
                      <button
                        onClick={handleProfileSave}
                        className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-blue-500/30 hover:bg-blue-700 transition flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" /> Save Changes
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 2. Work location prefrence */}
            <div className="bg-white rounded-[2rem] p-8 sm:p-6 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.02)] relative group transition-all duration-300 hover:shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <div className="flex justify-between items-start mb-8 relative z-10">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2.5">
                  <div className="p-2 bg-brand-500/20 rounded-xl text-brand-600">
                    <MapPin className="w-5 h-5" />
                  </div>
                  Work Location Preferences
                </h2>
                <button
                  onClick={() => {
                    setIsEditingLocations(!isEditingLocations);
                    if (!isEditingLocations) {
                      // Populate tempPreferredLocations with existing data when starting to edit
                      setTempPreferredLocations([...userPreferredLocations]);
                      // Store original values for change detection
                      setOriginalCurrentLocationId(selectedCurrentLocationId);
                      setOriginalPreferredLocations([...userPreferredLocations]);
                    }
                  }}
                  className={`text-sm font-bold px-4 py-2 rounded-xl transition-all flex items-center gap-2 ${isEditingLocations ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'text-brand-600 hover:bg-brand-50'}`}
                >
                  {isEditingLocations ? <><X className="w-4 h-4" /> Cancel</> : <><Pencil className="w-4 h-4" /> Edit</>}
                </button>
              </div>


              <div className="grid grid-cols-2 sm:grid-cols-1 gap-x-6 gap-y-5 relative z-10">
                {/* Current Location Section */}
                <div className="space-y-1.5 col-span-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Current Location</label>
                  {isEditingLocations ? (
                    <div className="relative location-dropdown">
                      <MapPin className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400 pointer-events-none z-10" />
                      <div className="relative">
                        <input
                          type="text"
                          value={currentLocationSearch}
                          onChange={(e) => {
                            setCurrentLocationSearch(e.target.value);
                            setShowCurrentLocationDropdown(true);
                          }}
                          onFocus={() => setShowCurrentLocationDropdown(true)}
                          placeholder="Search city..."
                          className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all shadow-inner cursor-pointer"
                        />
                        {currentLocationSearch ? (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedCurrentLocationId(null);
                              setSelectedCurrentLocationCity("");
                              setCurrentLocationSearch("");
                              setCurrentLocationDisplay("");
                            }}
                            className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        ) : (
                          <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                        )}
                      </div>
                      {showCurrentLocationDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-80 overflow-y-auto">
                          {locationsLoading ? (
                            <div className="p-3 text-sm text-slate-400">Loading locations...</div>
                          ) : filteredCurrentLocations.length > 0 ? (
                            filteredCurrentLocations.map(location => (
                              <div
                                key={location.id}
                                onClick={() => {
                                  setSelectedCurrentLocationId(location.id);
                                  setSelectedCurrentLocationCity(location.city);
                                  setCurrentLocationSearch(location.city);
                                  setShowCurrentLocationDropdown(false);
                                }}
                                className="p-1.5 px-4 text-sm font-semibold text-slate-800 hover:bg-brand-100 cursor-pointer transition-colors"
                              >
                                {location.city}
                              </div>
                            ))
                          ) : (
                            <div className="p-3 text-sm text-slate-400">No cities found</div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative">
                      <MapPin className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        disabled
                        value={currentLocationDisplay || "Not specified"}
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 cursor-not-allowed"
                      />
                    </div>
                  )}
                </div>

                {/* Preferred Locations Section (up to 5 locations) */}
                <div className="space-y-1.5 col-span-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex justify-between items-center">
                    <span>Preferred Locations</span>
                    {isEditingLocations && (
                      <span className="text-[10px] uppercase font-bold text-slate-400">
                        {tempPreferredLocations.length} / 5 selected
                      </span>
                    )}
                  </label>
                  {isEditingLocations ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-2xl border border-slate-200/60 min-h-[50px] items-center">
                        {tempPreferredLocations.length > 0 ? (
                          tempPreferredLocations.map((location) => (
                            <span key={location.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-50 border border-brand-100 text-brand-700 text-xs font-extrabold rounded-lg">
                              <MapPin className="w-3.5 h-3.5 text-brand-500" />
                              {location.city}
                              <button
                                type="button"
                                onClick={() => setTempPreferredLocations(tempPreferredLocations.filter(loc => loc.id !== location.id))}
                                className="text-brand-400 hover:text-brand-700 transition ml-0.5 rounded-full p-0.5 hover:bg-brand-100"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))
                        ) : (
                          <span className="text-xs font-semibold text-slate-400 py-1 px-1">Select up to 5 preferred work locations.</span>
                        )}
                      </div>
                      {tempPreferredLocations.length < 5 && (
                        <div className="relative location-dropdown">
                          <Plus className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400 pointer-events-none z-10" />

                          <div className="relative">
                            <input
                              type="text"
                              value={preferredLocationSearch}
                              onChange={(e) => {
                                setPreferredLocationSearch(e.target.value);
                                setShowPreferredLocationDropdown(true);
                              }}
                              onFocus={() => setShowPreferredLocationDropdown(true)}
                              placeholder="Search city..."
                              className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all shadow-inner cursor-pointer"
                            />
                            <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                          </div>
                          {showPreferredLocationDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-80 overflow-y-auto">
                              {locationsLoading ? (
                                <div className="p-3 text-sm text-slate-400">Loading locations...</div>
                              ) : filteredPreferredLocations.length > 0 ? (
                                filteredPreferredLocations.map(location => (
                                  <div
                                    key={location.id}
                                    onClick={() => {
                                      if (tempPreferredLocations.length < 5) {
                                        setTempPreferredLocations([...tempPreferredLocations, location]);
                                        setPreferredLocationSearch("");
                                        setShowPreferredLocationDropdown(false);
                                      }
                                    }}
                                    className="p-1.5 px-4 text-sm font-semibold text-slate-800 hover:bg-brand-100 cursor-pointer transition-colors"
                                  >
                                    {location.city}
                                  </div>
                                ))
                              ) : (
                                <div className="p-3 text-sm text-slate-400">No cities found</div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl min-h-[50px] items-center">
                      {userPreferredLocations.length > 0 ? (
                        userPreferredLocations.map((location) => (
                          <span key={location.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-50 border border-brand-100 text-brand-700 text-xs font-bold rounded-lg shadow-sm">
                            <MapPin className="w-3.5 h-3.5 text-brand-500" />
                            {location.city}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs font-medium text-slate-400 py-1 px-1">No preferred locations listed.</span>
                      )}
                    </div>
                  )

                  }
                </div>

                {isEditingLocations && (
                  <div className="col-span-2 sm:col-span-1 flex justify-end mt-2">
                    <button
                      onClick={async () => {
                        // Validation: require at least one preferred location
                        if (tempPreferredLocations.length === 0) {
                          showSnackbar("Please select at least one preferred location", "error");
                          return;
                        }

                        // Validation: current location cannot be removed
                        if (!selectedCurrentLocationId) {
                          showSnackbar("Add current location", "error");
                          return;
                        }

                        // Check what has changed
                        const currentLocationChanged = selectedCurrentLocationId !== originalCurrentLocationId;
                        const preferredLocationsChanged = JSON.stringify(tempPreferredLocations.map(l => l.id).sort()) !== JSON.stringify(originalPreferredLocations.map(l => l.id).sort());

                        // Only send API requests for changed fields
                        if (currentLocationChanged) {
                          await handleUpdateCurrentLocation(selectedCurrentLocationId, selectedCurrentLocationCity);
                        }
                        if (preferredLocationsChanged) {
                          await handleUpdatePreferredLocations();
                        }

                        setIsEditingLocations(false);
                      }}
                      className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-brand-500/20 transition flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" /> Save Locations
                    </button>
                  </div>
                )}
              </div>
            </div>



            {/* 3. Educational Qualification */}
            <div className="bg-white rounded-[2rem] p-8 sm:p-6 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.02)]">
              <div className="flex justify-between items-center mb-8 gap-8">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2.5">
                  <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                    <GraduationCap className="w-5 h-5" />
                  </div>
                  Education
                </h2>
                {!isAddingEdu && !editingEduId && (
                  <button
                    onClick={handleAddNewClick}
                    className="text-sm font-bold bg-slate-900 text-white hover:bg-slate-800 px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 shadow-lg shadow-slate-900/20"
                  >
                    <Plus className="w-4 h-4" /> Add Education
                  </button>
                )}
              </div>
              {/* ADD / EDIT FORM */}
              {isAddingEdu && (
                <div className="bg-slate-50 rounded-2xl p-6 border border-blue-100 animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2">
                      <div className="w-2 h-6 bg-blue-600 rounded-full"></div>
                      {isAddingEdu
                        ? "Add New Qualification"
                        : "Edit Qualification"}
                    </h3>
                    <button
                      onClick={handleCancelEdu}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-12 sm:grid-cols-1 gap-5">
                    <div className="space-y-1.5 col-span-12">
                      <label className="text-xs font-bold text-slate-500">
                        Qualification
                      </label>
                      <div className="relative">
                        <select
                          className="w-full appearance-none p-3 rounded-xl border border-slate-300 bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          value={eduForm.qualification}
                          onChange={handleQualificationChange}
                        >
                          {QUALIFICATION_OPTIONS.map((q) => (
                            <option key={q.id} value={q.value}>
                              {q.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-5 col-span-12 sm:grid-cols-1">
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-500">
                          Course
                        </label>
                        <div className="relative">
                          <select
                            className="w-full appearance-none p-3 rounded-xl border border-slate-300 bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            value={eduForm.course_name}
                            onChange={handleCourseChange}
                          >
                            {courseOptions.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-500">
                          Specialization
                        </label>
                        <div className="relative">
                          <select
                            className="w-full appearance-none p-3 rounded-xl border border-slate-300 bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            value={eduForm.specialization_name}
                            onChange={(e) =>
                              setEduForm({
                                ...eduForm,
                                specialization_name: e.target.value,
                              })
                            }
                          >
                            {specializationOptions.length > 0 ? (
                              specializationOptions.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))
                            ) : (
                              <option value="" disabled>
                                No specializations available
                              </option>
                            )}
                          </select>
                          <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>
                      </div>
                    </div>
                    {/* College Auto Search */}
                    <div className="space-y-1.5 col-span-12 relative">
                      <label className="text-xs font-bold text-slate-500">
                        College / Institute
                      </label>
                      <div className="relative">
                        <Search className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          className="w-full pl-9 p-3 rounded-xl border border-slate-300 bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          value={collegeSearch}
                          onChange={(e) => {
                            setCollegeSearch(e.target.value);
                            setShowCollegeDropdown(true);
                            setEduForm({
                              ...eduForm,
                              college: e.target.value,
                              college_master_id: "",
                            });
                          }}
                          onFocus={() => setShowCollegeDropdown(true)}
                          placeholder="Search for college..."
                        />
                      </div>
                      {/* Dropdown */}
                      {showCollegeDropdown && collegeSearch.length >= 2 && (
                        <ul className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto no-scrollbar">
                          {collegeLoading ? (
                            <li className="px-4 py-3 text-sm text-slate-400 italic">
                              Searching...
                            </li>
                          ) : collegeResults.length > 0 ? (
                            collegeResults.map((col, idx) => (
                              <li
                                key={idx}
                                onClick={() => selectCollege(col)}
                                className="px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 cursor-pointer border-b border-slate-50 last:border-0"
                              >
                                {col.college_name}
                              </li>
                            ))
                          ) : (
                            <li className="px-4 py-3 text-sm text-slate-400 italic">
                              No college found.
                            </li>
                          )}
                        </ul>
                      )}
                      {showCollegeDropdown &&
                        collegeSearch.length > 0 &&
                        collegeSearch.length < 2 && (
                          <ul className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl">
                            <li className="px-4 py-3 text-sm text-slate-400 italic">
                              Type at least 2 characters to search...
                            </li>
                          </ul>
                        )}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-1 gap-5 col-span-12">
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-500">
                          Passout Year
                        </label>
                        <div className="relative">
                          <select
                            className="w-full appearance-none p-3 rounded-xl border border-slate-300 bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            value={eduForm.passout_year + ""}
                            onChange={(e) =>
                              setEduForm({
                                ...eduForm,
                                passout_year: e.target.value,
                              })
                            }
                          >
                            {Array.from(
                              { length: 2030 - 2020 + 1 },
                              (_, i) => 2020 + i,
                            ).map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-500">
                          Type
                        </label>
                        <div className="flex w-full gap-2 p-1.5 bg-white border border-slate-200 rounded-xl h-[46px]">
                          {["FULLTIME", "PARTTIME"].map((t) => (
                            <button
                              key={t}
                              onClick={() =>
                                setEduForm({ ...eduForm, course_type: t })
                              }
                              className={`flex-1 flex items-center justify-center w-full rounded-lg text-xs font-bold transition-all whitespace-nowrap ${eduForm.course_type === t ? "bg-blue-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-50"}`}
                            >
                              {t === "FULLTIME"
                                ? "Full Time"
                                : t === "PARTTIME"
                                  ? "Part Time"
                                  : t}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-8">
                    <button
                      onClick={handleCancelEdu}
                      className="px-5 py-2.5 rounded-xl text-slate-600 font-bold hover:bg-slate-200 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdu}
                      className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all text-sm flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" /> Save Details
                    </button>
                  </div>
                </div>
              )}
              {/* // {console.log("education list", educationList)} */}
              <div className="space-y-6">
                {/* LIST VIEW */}
                {educationList.map((edu) => {
                  // // console.log("edu map", edu);
                  if (editingEduId === edu.id)
                    return (
                      <div
                        key={edu.id}
                        className="bg-slate-50 rounded-2xl p-6 border border-blue-100 animate-in fade-in slide-in-from-bottom-4 duration-300"
                      >
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="font-bold text-slate-900 flex items-center gap-2">
                            <div className="w-2 h-6 bg-blue-600 rounded-full"></div>
                            {isAddingEdu
                              ? "Add New Qualification"
                              : "Edit Qualification"}
                          </h3>
                          <button
                            onClick={handleCancelEdu}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="grid grid-cols-12 sm:grid-cols-1 gap-5">
                          <div className="space-y-1.5 col-span-12">
                            <label className="text-xs font-bold text-slate-500">
                              Qualification
                            </label>
                            <div className="relative">
                              <select
                                className="w-full appearance-none p-3 rounded-xl border border-slate-300 bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                value={eduForm.qualification}
                                onChange={handleQualificationChange}
                              >
                                {QUALIFICATION_OPTIONS.map((q) => (
                                  <option key={q.id} value={q.value}>
                                    {q.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-5 col-span-12  sm:grid-cols-1">
                            <div className="space-y-1.5">
                              <label className="text-xs font-bold text-slate-500">
                                Course
                              </label>
                              <div className="relative">
                                <select
                                  className="w-full appearance-none p-3 rounded-xl border border-slate-300 bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                  value={eduForm.course_name}
                                  onChange={handleCourseChange}
                                >
                                  {courseOptions.map((c) => (
                                    <option key={c} value={c}>
                                      {c}
                                    </option>
                                  ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <label className="text-xs font-bold text-slate-500">
                                Specialization
                              </label>
                              <div className="relative">
                                <select
                                  className="w-full appearance-none p-3 rounded-xl border border-slate-300 bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                  value={eduForm.specialization_name}
                                  onChange={(e) =>
                                    setEduForm({
                                      ...eduForm,
                                      specialization_name: e.target.value,
                                    })
                                  }
                                >
                                  {specializationOptions.length > 0 ? (
                                    specializationOptions.map((s) => (
                                      <option key={s} value={s}>
                                        {s}
                                      </option>
                                    ))
                                  ) : (
                                    <option value="" disabled>
                                      No specializations available
                                    </option>
                                  )}
                                </select>
                                <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                              </div>
                            </div>
                          </div>

                          {/* College Auto Search */}
                          <div className="space-y-1.5 col-span-12 relative">
                            <label className="text-xs font-bold text-slate-500">
                              College / Institute
                            </label>
                            <div className="relative">
                              <Search className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                              <input
                                type="text"
                                className="w-full pl-9 p-3 rounded-xl border border-slate-300 bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                value={collegeSearch}
                                onChange={(e) => {
                                  setEduForm({
                                    ...eduForm,
                                    college: e.target.value,
                                    college_master_id: "",
                                    // college_master_id: edu.college_master_id,
                                  });
                                  setCollegeSearch(e.target.value);
                                  setShowCollegeDropdown(true);
                                }}
                                onFocus={() => setShowCollegeDropdown(true)}
                                placeholder="Search for college..."
                              />
                            </div>
                            {/* Dropdown */}
                            {showCollegeDropdown &&
                              collegeSearch.length >= 2 && (
                                <ul className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto no-scrollbar">
                                  {collegeLoading ? (
                                    <li className="px-4 py-3 text-sm text-slate-400 italic">
                                      Searching...
                                    </li>
                                  ) : collegeResults.length > 0 ? (
                                    collegeResults.map((col, idx) => (
                                      <li
                                        key={idx}
                                        onClick={() => selectCollege(col)}
                                        className="px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 cursor-pointer border-b border-slate-50 last:border-0"
                                      >
                                        {col.college_name}
                                      </li>
                                    ))
                                  ) : (
                                    <li className="px-4 py-3 text-sm text-slate-400 italic">
                                      No college found.
                                    </li>
                                  )}
                                </ul>
                              )}
                            {showCollegeDropdown &&
                              collegeSearch.length > 0 &&
                              collegeSearch.length < 2 && (
                                <ul className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl">
                                  <li className="px-4 py-3 text-sm text-slate-400 italic">
                                    Type at least 2 characters to search...
                                  </li>
                                </ul>
                              )}
                          </div>

                          <div className="grid grid-cols-2 gap-5 col-span-12 sm:grid-cols-1">
                            <div className="space-y-1.5">
                              <label className="text-xs font-bold text-slate-500">
                                Passout Year
                              </label>
                              <div className="relative">
                                <select
                                  className="w-full appearance-none p-3 rounded-xl border border-slate-300 bg-white text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                  value={eduForm.passout_year + ""}
                                  onChange={(e) =>
                                    setEduForm({
                                      ...eduForm,
                                      passout_year: e.target.value,
                                    })
                                  }
                                >
                                  {Array.from(
                                    {
                                      length: 2030 - 2020 + 1,
                                    },
                                    (_, i) => 2020 + i,
                                  ).map((y) => (
                                    <option key={y} value={y}>
                                      {y}
                                    </option>
                                  ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <label className="text-xs font-bold text-slate-500">
                                Type
                              </label>
                              <div className="flex w-full gap-2 p-1.5 bg-white border border-slate-200 rounded-xl h-[46px]">
                                {["FULLTIME", "PARTTIME"].map((t) => (
                                  <button
                                    key={t}
                                    onClick={() =>
                                      setEduForm({ ...eduForm, course_type: t })
                                    }
                                    className={`flex-1 flex items-center justify-center w-full rounded-lg text-xs font-bold transition-all whitespace-nowrap ${eduForm.course_type === t ? "bg-blue-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-50"}`}
                                  >
                                    {t === "FULLTIME"
                                      ? "Full Time"
                                      : t === "PARTTIME"
                                        ? "Part Time"
                                        : t}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                          <button
                            onClick={handleCancelEdu}
                            className="px-5 py-2.5 rounded-xl text-slate-600 font-bold hover:bg-slate-200 transition-colors text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveEdu}
                            className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all text-sm flex items-center gap-2"
                          >
                            <Check className="w-4 h-4" /> Save Details
                          </button>
                        </div>
                      </div>
                    );
                  return (
                    <div
                      key={edu.id}
                      className="group p-5 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all relative overflow-hidden"
                    >
                      {/* <div className="absolute top-0 right-0 p-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 backdrop-blur-sm rounded-bl-2xl">
                        <button
                          onClick={() => handleEditEdu(edu)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEdu(edu.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div> */}

                      <div className="flex items-start gap-5">
                        <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 border border-blue-100">
                          <Building className="w-7 h-7" />
                        </div>
                        <div className="grow">
                          <h3 className="font-bold text-slate-900 text-lg">
                            {edu.course_name}{" "}
                            <span className="text-slate-400 font-normal mx-1">
                              •
                            </span>{" "}
                            <span className="text-blue-600">
                              {edu.specialization_name}
                            </span>
                          </h3>
                          <p className="text-slate-600 font-medium mt-1">
                            {edu.college_name || edu.college}
                          </p>
                          <div className="flex justify-between items-center flex-wrap">
                            <div className="flex items-center gap-4 mt-3 text-xs font-bold text-slate-500 uppercase tracking-wide">
                              <span className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
                                <Calendar className="w-3.5 h-3.5" />{" "}
                                {edu.passout_year}
                              </span>
                              <span className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
                                <CheckCircle2 className="w-3.5 h-3.5" />{" "}
                                {edu.course_type === "FULLTIME"
                                  ? "Full Time"
                                  : "Part Time"}
                              </span>
                            </div>

                            <button
                              onClick={() => handleEditEdu(edu)}
                              className=" text-sm px-5 py-2.5 rounded-xl  text-blue-600 hover:bg-blue-50 transition"
                            >
                              Update
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Resume (4 cols) */}
          <div className="col-span-4 xl:col-span-1 space-y-8">
            {/* Resume Card */}
            <div className="bg-white rounded-[2rem] p-8 sm:p-6 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.02)] h-fit sticky top-24">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2.5 mb-6">
                <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                  <FileText className="w-5 h-5" />
                </div>
                Resume
              </h2>

              <input
                type="file"
                ref={resumeInputRef}
                className="hidden"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleResumeChange}
              />

              {resume ? (
                // Resume Present State
                <div className="bg-slate-900 rounded-2xl p-5 text-white relative overflow-hidden group">
                  {/* Abstract Pattern */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500 rounded-full blur-[40px] opacity-20 -mr-10 -mt-10"></div>

                  <div className="flex items-start gap-4 relative z-10">
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center border border-white/10">
                      <FileText className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate pr-6">
                        {resume.name}
                      </div>
                      <div className="text-xs text-slate-400 mt-1 font-medium">
                        {resume.size} • {resume.date}
                      </div>
                    </div>
                    <button
                      onClick={handleResumeDelete}
                      className="text-slate-400 hover:text-red-400 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-6 flex gap-3">
                    <button
                      onClick={handleResumeDownload}
                      className="flex-1 bg-white text-slate-900 py-2.5 rounded-xl text-xs font-bold hover:bg-slate-100 transition flex items-center justify-center gap-2"
                    >
                      <Download className="w-3.5 h-3.5" /> Download
                    </button>
                    <button
                      onClick={handleResumeClick}
                      className="flex-1 bg-white/10 text-white py-2.5 rounded-xl text-xs font-bold hover:bg-white/20 transition flex items-center justify-center gap-2"
                    >
                      <UploadCloud className="w-3.5 h-3.5" /> Replace
                    </button>
                  </div>
                </div>
              ) : (
                // Upload State
                <div
                  onClick={handleResumeClick}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-3xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all group min-h-[250px] ${isDragging
                    ? "border-blue-500 bg-blue-50/50"
                    : "border-slate-200 hover:border-blue-500 hover:bg-blue-50/50"
                    }`}
                >
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-all shadow-sm ${isDragging
                      ? "bg-white text-blue-600 shadow-md"
                      : "bg-slate-50 text-slate-400 group-hover:bg-white group-hover:text-blue-600 group-hover:shadow-md"
                      }`}
                  >
                    <UploadCloud className="w-8 h-8" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-1">
                    Upload Resume
                  </h3>
                  <p className="text-xs text-slate-500 font-medium max-w-[200px] leading-relaxed">
                    Drop your file here or{" "}
                    <span className="text-blue-600 underline">browse</span>
                  </p>
                  <p className="mt-4 text-[10px] text-slate-400 font-bold uppercase tracking-wider bg-slate-50 px-3 py-1 rounded-full">
                    PDF, DOCX Max 2MB
                  </p>
                </div>
              )}

              <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
                <div className="mt-0.5">
                  <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3" />
                  </div>
                </div>
                <div className="text-xs text-blue-800 font-medium leading-relaxed">
                  <span className="font-bold">Pro Tip:</span> Recruiters are 3x
                  more likely to contact candidates with a verified resume.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={snackbarStyle}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteEdu}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this qualification?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteEdu}>Cancel</Button>
          <Button onClick={confirmDeleteEdu} autoFocus color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
